<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted } from "vue";
import { useLayout, useVersion } from "@/composables";
import { useAppState } from "@/store/appState";
import { Layout } from "@/router";

const AppLoader = defineAsyncComponent(() => import("@/components/generics/AppLoader.vue"));
const DialogConfirmation = defineAsyncComponent(() => import("@/components/basics/DialogConfirmation.vue"));
const OverlayMessage = defineAsyncComponent(() => import("@/components/OverlayMessage.vue"));
const GalleryFullscreen = defineAsyncComponent(() => import("@/components/generics/GalleryFullscreen.vue"));
const NewReleaseBar = defineAsyncComponent(() => import("@/components/generics/NewReleaseBar.vue"));
const PopupNotifications = defineAsyncComponent(() => import("@/components/popupNotifications/PopupNotifications.vue"));
const CookieConsent = defineAsyncComponent(() => import("@/components/generics/CookieConsent.vue"));

// Layout
const { layout } = useLayout();
const Layout_Default = defineAsyncComponent(() => import("@/components/layouts/LayoutDefault.vue"));
const Layout_LockingState = defineAsyncComponent(() => import("@/components/layouts/LayoutLockingState.vue"));

// Page initialization
const appStateStore = useAppState();
appStateStore.startupUserCheck();

// Latest bundle version check
const version = useVersion();
version.logIntoConsole();
version.startAutocheck();
const renderNewReleaseBar = computed(() => version.isLatest.value === false);

// CSS helpers
const isSomeNotificationVisible = computed(() => renderNewReleaseBar.value);
const pageWrapperCssClasses = computed(() => ({
  "page-wrapper": true,
  "with-notification": isSomeNotificationVisible.value,
}));
const renderLayout = computed(() => appStateStore.isAppInitialized() && layout !== undefined);

// Async styles import - we dont want them to be "blocking", however we want them to be loaded everywhere.
// Maybe it's not a bad idea to handle it in a build process via Vite -> no ROI for now
onMounted(async () => await import("floating-vue/dist/style.css"));
onMounted(async () => await import("element-plus/es/components/loading/style/css"));
</script>

<template>
  <NewReleaseBar v-if="renderNewReleaseBar" />

  <transition name="fade-500">
    <div v-if="renderLayout" :class="pageWrapperCssClasses">
      <Layout_Default v-if="layout === Layout.Default" />
      <Layout_LockingState v-else-if="layout === Layout.LockingState" />
    </div>
    <AppLoader v-else />
  </transition>

  <GalleryFullscreen />
  <DialogConfirmation />
  <OverlayMessage />
  <PopupNotifications />
  <CookieConsent />
</template>

<style lang="scss" scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @include mixins.maxScreenWidth(md) {
    min-height: 100dvh;
  }

  &.with-notification {
    padding-top: var(--notificationBarHeight);
  }
}
</style>
